import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom, from, throwError } from 'rxjs';
import { IdentityApiService, type IdentityData, type ProfilePatch, type UserProfile } from '@lite-workspace/shared/identity';
import type { MessengerNetwork } from '@lite-workspace/dto-user';
import { AccountService } from './account.service';

/**
 * Identity source for the gallery cabinet: the account row of /api/users/me/profile, the same one
 * the other cabinets read — the BFF session's whoami cannot supply the email (its `email` is copied
 * from an `email` claim on the Hydra access token, which the exchange does not mint), so a
 * session-sourced account line was always blank under BFF. Name and contacts are the account's own,
 * so the settings page saves them straight back through the shared API.
 * The remaining write surfaces (password, onboarding) belong to fotoclass and have no counterpart here.
 */
@Injectable()
export class GalleryIdentityData implements IdentityData {
  private readonly account = inject(AccountService);
  private readonly api = inject(IdentityApiService);

  getProfile(): Observable<UserProfile> {
    // The visitka summary is primed alongside: the cabinet header's avatar and the cover credit
    // line read it, and nothing else loads it on a deep link.
    const both = Promise.all([this.account.load(), firstValueFrom(this.api.getProfile())]);
    return from(both.then(([, profile]) => profile));
  }

  updateProfile(patch: ProfilePatch): Observable<UserProfile> {
    return this.api.updateProfile(patch);
  }

  connectMessenger(network: MessengerNetwork): Observable<string> {
    return this.api.connectMessenger(network);
  }

  disconnectMessenger(network: MessengerNetwork): Observable<void> {
    return this.api.disconnectMessenger(network);
  }

  setPassword(): Observable<void> {
    return throwError(() => new Error('Пароль не меняется из кабинета галерей'));
  }

  dismissOnboardingFlow(): Observable<never> {
    return throwError(() => new Error('Онбординг не живёт в кабинете галерей'));
  }

  // Every onboarding write is refused for the same reason, so they share the one refusal.
  completeOnboardingFlow(): Observable<never> {
    return this.dismissOnboardingFlow();
  }

  markLessonViewed(): Observable<never> {
    return this.dismissOnboardingFlow();
  }

  unmarkLessonViewed(): Observable<never> {
    return this.dismissOnboardingFlow();
  }

  markLessonFavorite(): Observable<never> {
    return this.dismissOnboardingFlow();
  }

  unmarkLessonFavorite(): Observable<never> {
    return this.dismissOnboardingFlow();
  }
}
